import { FC, useEffect, useState } from 'react';
import { Loader } from '@livechat/design-system-react-components';
import AccountsSDK from '@livechat/accounts-sdk';
import Cookies from 'js-cookie';

import Config from 'Config';
import { Layout } from 'components/layout/Layout';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { fetchTokenDetails } from 'hooks/use-get-token-details';
import Location from 'services/Location';
import { organizationsClient } from 'services/connectivity/organizations/client';
import { accountsClient } from 'services/connectivity/accounts/client';
import { productsClient } from 'services/connectivity/products/client';
import { errorDeserialize } from 'helpers/error-deserializer';
import { getAccessToken } from 'helpers/account';

import { JoinProduct } from '../chooser/join-product/JoinProduct';

import { RequestAccess } from './request-access/RequestAccess';

import * as styles from './styles';

type Props = {
  flow: string;
  identityException: string;
  oauthException: string;
  exceptionDetails: string;
  requestId: string;
  traceId?: string;
};

export const Ooops: FC<Props> = ({
  flow,
  identityException,
  oauthException,
  exceptionDetails,
  requestId,
  traceId,
}) => {
  const [requestedProduct, setRequestedProduct] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [hasOrganizationWhitelistedDomain, setHasOrganizationWhitelistedDomain] =
    useState<boolean>(false);
  const [organizationDomains, setOrganizationDomains] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isMeOwner, setIsMeOwner] = useState<boolean>(false);

  useEffect(() => {
    const checkTokenAndGetData = async () => {
      if (oauthException === 'invalid_scope') {
        let token = getAccessToken();

        try {
          if (!token) {
            const sdk = new AccountsSDK({
              client_id: Config.organization_app_client_id,
              server_url: Config.accounts_url,
            });
            const response = await sdk.iframe().authorize();
            const tokenDetails = await fetchTokenDetails(response.access_token);
            token = tokenDetails.access_token;
            Cookies.set('access_token', response.access_token, {
              secure: true,
              sameSite: 'none',
            });
          } else {
            await fetchTokenDetails(token);
          }
        } catch (error) {
          // Account is not authorized, skip the flow
          setIsLoaded(true);
          return;
        }

        setAccessToken(token);

        const { result: currentOrganization, error: organizationError } =
          await organizationsClient.getMy(token);

        if (organizationError) {
          setIsLoaded(true);
          throw new Error(
            `Organization data couldn't be fetched. [${errorDeserialize(organizationError)}]`
          );
        }

        const { result: products, error: productsError } = await productsClient.list();
        if (productsError) {
          setIsLoaded(true);
          throw new Error(
            `Products data couldn't be fetched. [${errorDeserialize(productsError)}]`
          );
        }

        const { result: domainsResult, error: domainsError } =
          await organizationsClient.getMyDomains(token);
        if (domainsError) {
          setIsLoaded(true);
          throw new Error(`Domains data couldn't be fetched. [${errorDeserialize(domainsError)}]`);
        }

        const { result: me, error: meError } = await accountsClient.getMe(token);
        if (meError) {
          setIsLoaded(true);
          throw new Error(`Me data couldn't be fetched. [${errorDeserialize(meError)}]`);
        }

        try {
          const productToRequest = products.find(
            (productData) =>
              productData.client_id === Location.getUrlParam('client_id') &&
              currentOrganization?.products?.some(
                (organizationProduct) => organizationProduct.product === productData.product
              )
          );

          setRequestedProduct(productToRequest?.product || '');
          setOrganizationId(currentOrganization?.organization_id);
          setHasOrganizationWhitelistedDomain(currentOrganization?.domain_whitelist);
          setOrganizationDomains(domainsResult?.map((domain) => domain.domain));
          setUserEmail(me?.email);
          setIsMeOwner(
            me?.roles?.some(
              (role) =>
                role.organization_id === currentOrganization.organization_id &&
                role.product === 'Accounts' &&
                role.role === 'owner'
            )
          );
        } catch (e) {
          setIsLoaded(true);
          throw new Error(`State couldn't be set. [${errorDeserialize(e)}]`);
        }

        setIsLoaded(true);
      } else {
        setIsLoaded(true);
      }
    };

    checkTokenAndGetData();
  }, [oauthException]);

  const loginButtonAppError = () => {
    const error = {
      identity_exception: identityException,
      oauth_exception: oauthException,
      exception_details: exceptionDetails,
    };

    Location.sendPostMessage({
      error,
    });

    return null;
  };

  const classicAppError = () => {
    let description: string;

    switch (oauthException) {
      case 'invalid_request':
        description =
          'The request is missing a required parameter, includes an invalid parameter value, includes a parameter more than once, or is otherwise malformed.';
        break;

      case 'unauthorized_client':
        description = 'The client is not authorized to request a token using this method.';
        break;

      case 'access_denied':
        description = 'The resource owner or authorization server denied the request.';
        break;

      case 'unsupported_response_type':
        description =
          'The authorization server does not support obtaining a token using this method.';
        break;

      case 'invalid_scope':
        description = 'The requested scope is invalid, insufficient, unknown or malformed.';
        break;

      case 'server_error':
        description =
          'The authorization server encountered an unexpected condition that prevented it from fulfilling the request.';
        break;

      case 'temporarily_unavailable':
        description =
          'The authorization server is currently unable to handle the request due to a temporary overloading or maintenance of the server.';
        break;

      case 'unsupported_grant_type':
        description = 'The authorization grant type is not supported by the authorization server.';
        break;

      case 'invalid_grant':
        description =
          'The provided authorization grant (e.g., authorization code, resource owner credentials) or refresh token is invalid, expired, revoked, does not match the redirection URI used in the authorization request, or was issued to another client.';
        break;

      case 'invalid_client':
        description =
          'Client authentication failed (e.g., unknown client, no client authentication included, or unsupported authentication method).';
        break;

      default:
        break;
    }

    let exceptionMessage: string;
    switch (exceptionDetails) {
      case 'client_id_not_found':
        exceptionMessage = 'wrong client id, client id does not exists';
        break;

      case 'redirect_uri_not_set':
        exceptionMessage = 'client misconfiguration, client has not set redirect uri';
        break;

      case 'invalid_redirect_uri':
        exceptionMessage = "redirect uri is not one of client's allowed redirects";
        break;

      case 'too_many_redirects':
        exceptionMessage =
          'We experienced too many requests in a short period. Please wait a moment and try again.';
        break;

      default:
        break;
    }

    const canUserJoinProduct =
      (hasOrganizationWhitelistedDomain &&
        organizationDomains.some((domain) => domain === userEmail.split('@')[1])) ||
      isMeOwner;

    if (isLoaded === false) {
      return (
        <div css={styles.loaderContainer}>
          <Loader size="large" />
        </div>
      );
    }

    if (oauthException === 'invalid_scope' && requestedProduct) {
      return canUserJoinProduct ? (
        <JoinProduct currentProduct={requestedProduct} organizationId={organizationId} />
      ) : (
        <Layout showHeaderLogo showDropdown>
          <RequestAccess productName={requestedProduct} accessToken={accessToken} />
        </Layout>
      );
    }

    return (
      <UnexpectedError
        description={description ?? ''}
        requestId={requestId}
        traceId={traceId}
        exceptionDetails={exceptionMessage ?? ''}
        oauthException={oauthException}
      />
    );
  };

  if (flow === 'button') {
    return loginButtonAppError();
  }

  return classicAppError();
};
